import * as React from 'react';
import {
    getLatestMessageTimestamp,
    getLiveCatalogData,
    getLiveItemData,
    getPubnubPublishKey,
} from '@/redux/modules/console';
import { getUiLiveBid, submitLiveBid } from '@/redux/modules/liveBid';
import { getUserData, getUserIsInGoodStanding, isUserLoggedIn } from '@/redux/modules/user';
import { ModalContext, openLoginModal, openRegisterForAuctionModal } from '@/redux/modules/modal';
import { sendDismissHoverMessage, sendHoverMessage } from '@/redux/modules/bidHover';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import classNames from 'classnames';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { faUnlock } from '@fortawesome/pro-solid-svg-icons/faUnlock';
import { fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import { FormattedMessage } from 'react-intl';
import { getBidLimitBalance } from '@/redux/modules/bidLimit';
import { getCatalog } from '@/redux/modules/catalog';
import { getCatalogRegistration } from '@/redux/modules/catalogRegistration';
import { getConsoleBranding } from '@/redux/modules/consoleBranding';
import { getCountDown } from '@/redux/modules/bidCountDown';
import { getDeskSupportLink } from '@/utils/urls';
import { getLocale } from '@/redux/modules/intl';
import { getUiHasLoggedInBefore } from '@/redux/modules/autoPopModal';
import HoverIntent from '@liveauctioneers/react-hoverintent';
import isEqual from 'react-fast-compare';
import { isUiMobile } from '@/redux/modules/browser';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import ms from 'ms';
import noop from '@/utils/noop';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import styles from './BiddingConsoleButton.module.scss';
import Switch from 'react-switch';
import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import variables from '../../theme/styles.scss';

// slider/switch component consts
const handleDiameter = 36;
const sliderHeight = 30;
const sliderWidth = 64;
const sliderBoxShadow = `0 1px 5px ${theme.colors.shadow500}`;
const sliderActiveBoxShadow = `0 0 1px 10px ${theme.colors.shadow200}`;
const uncheckedIcon = <FontAwesomeIcon className={styles.openLockIcon} icon={faUnlock} />;
const checkedIcon = <FontAwesomeIcon className={styles.lockIcon} icon={faLock} />;

type Props = {
    biddingLocked?: boolean;
    catalogId: number;
    itemId: number;
    noLinks?: boolean;
    toggleBiddingLocked?: (event: any) => void;
};

const BiddingConsoleButton = (props: Props) => {
    const dispatch = useDispatch();
    const { biddingLocked, catalogId, itemId, noLinks, toggleBiddingLocked } = props;

    const userLoggedIn = useSelector((state) => isUserLoggedIn(state), isEqual);
    const accountInGoodStanding = useSelector((state) => getUserIsInGoodStanding(state), isEqual);
    const bidLimitBalance = useSelector((state) => getBidLimitBalance(state, catalogId, itemId));
    const catalogRegistration = useSelector((state) => getCatalogRegistration(state, catalogId), isEqual);
    const liveCatalogData = useSelector((state) => getLiveCatalogData(state, catalogId), isEqual);
    const {
        activeBidder,
        bidderOutbid,
        closed,
        currentItem,
        networkStatus,
        status: liveCatalogStatus,
        won,
    } = liveCatalogData;
    const { currency = '', isTimed = false, sellerId } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const { color: consoleBrandingColor } = useSelector(
        (state) => getConsoleBranding(state, catalogId, sellerId),
        isEqual
    );
    const timeStamp = useSelector((state) => getCountDown(state, itemId), isEqual);
    const hasLoggedInBefore = useSelector((state) => getUiHasLoggedInBefore(state));
    const isMobileDevice = useSelector((state) => Boolean(isUiMobile(state)));
    const liveUpdateTimestamp = useSelector((state) => getLatestMessageTimestamp(state, catalogId));
    const locale = useSelector((state) => getLocale(state));
    const { bidderId } = useSelector((state) => getUserData(state));
    const { currentAskPrice, leadingBidder } = useSelector((state) => getLiveItemData(state, itemId));
    const readyToBid = useSelector((state) => Boolean(getPubnubPublishKey(state, catalogId)));
    const { submitted } = useSelector((state) => getUiLiveBid(state));

    const myBid = bidderId === leadingBidder;
    const { approved, declined, limited, pending, suspended } = catalogRegistration;
    const showCountDown = isTimed && Boolean(timeStamp) && approved;

    const [hovering, setHovering] = React.useState(false);
    const [, setTime] = React.useState(0);

    React.useEffect(() => {
        // bump ts to re-render the app at least once every 5 seconds
        const interval = setInterval(() => setTime(Date.now()), 5000);
        return () => {
            if (hovering) {
                dispatch(sendDismissHoverMessage(Date.now(), catalogId, currentAskPrice, currency, itemId));
            }
            clearInterval(interval);
        };
    }, [catalogId, currency, currentAskPrice, dispatch, hovering, itemId]);

    const placeBid = () => {
        // dont only rely on the "disabled" button to prevent bidders who havent been approved
        if (accountInGoodStanding && (approved || limited) && !biddingLocked && checkReadyToBid()) {
            dispatch(submitLiveBid(Date.now(), catalogId, currentAskPrice, currency, itemId));
        } else {
            return false;
        }
    };

    const handleMouseOver = (ready: boolean, wouldExceedBidLimit: boolean) => {
        if (!isMobileDevice && !submitted && ready && !wouldExceedBidLimit) {
            return localSendHoverMessage;
        }
        return noop;
    };

    const handleMouseOut = (ready: boolean, wouldExceedBidLimit: boolean) => {
        if (!isMobileDevice && !submitted && ready && !wouldExceedBidLimit) {
            return localSendDismissHoverMessage;
        }
        return noop;
    };

    const localSendHoverMessage = () => {
        if (hovering) {
            dispatch(sendDismissHoverMessage(Date.now(), catalogId, currentAskPrice, currency, itemId));
            dispatch(sendHoverMessage(Date.now(), catalogId, currentAskPrice, currency, itemId));
        } else {
            setHovering(true);
            dispatch(sendHoverMessage(Date.now(), catalogId, currentAskPrice, currency, itemId));
        }
    };

    const localSendDismissHoverMessage = () => {
        if (!hovering) {
            dispatch(sendHoverMessage(Date.now(), catalogId, currentAskPrice, currency, itemId));
            dispatch(sendDismissHoverMessage(Date.now(), catalogId, currentAskPrice, currency, itemId));
        } else {
            setHovering(false);
            dispatch(sendDismissHoverMessage(Date.now(), catalogId, currentAskPrice, currency, itemId));
        }
    };

    const showRegisterForAuctionModal = React.useCallback(
        (attrs) => {
            dispatch(openRegisterForAuctionModal(attrs));
        },
        [dispatch]
    );

    const showLoginModal = React.useCallback(() => {
        dispatch(openLoginModal());
    }, [dispatch]);

    const checkReadyToBid = () => {
        const timeSinceLastMessage = Date.now() - liveUpdateTimestamp;

        if (timeSinceLastMessage > ms('4m') && Boolean(catalogId)) {
            dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, true));
        }

        return (
            readyToBid &&
            itemId === currentItem &&
            itemId > 0 &&
            networkStatus === 'connected' &&
            liveCatalogStatus === 'live' &&
            !closed &&
            !won &&
            Boolean(currentAskPrice) &&
            catalogId > 0 &&
            timeSinceLastMessage < ms('5m')
        );
    };

    const auctionPaused = liveCatalogStatus === 'paused';

    // ensure that we dont enable the button unless we have the required info
    const ready = checkReadyToBid();
    const wouldExceedBidLimit = limited && currentAskPrice > bidLimitBalance;

    const formattedAsk = (
        <FormattedCurrency className={styles.currency} noFraction sourceCurrency={currency} value={currentAskPrice} />
    );

    const bidMessage = <FormattedMessage id="bidding_console.console.button.bid" values={{ amount: formattedAsk }} />;

    const outbidMessage = (
        <FormattedMessage id="bidding_console.console.button.outbid" values={{ amount: formattedAsk }} />
    );

    const brandedButtonStyle =
        consoleBrandingColor !== ''
            ? {
                  background: consoleBrandingColor,
                  backgroundColor: consoleBrandingColor,
                  borderBottom: consoleBrandingColor,
                  borderColor: consoleBrandingColor,
                  borderTop: consoleBrandingColor,
              }
            : {};

    let button;

    const modalContext: ModalContext = {
        id: catalogId,
        type: 'register for auction',
    };

    if (!userLoggedIn) {
        const loginTextId = hasLoggedInBefore
            ? 'bidding_console.console.button.login'
            : 'bidding_console.console.button.signup';

        const classes = classNames(styles.tertiary, styles['action-button']);
        button = (
            <Button className={classes} locale={locale} onClick={showLoginModal} primary red style={brandedButtonStyle}>
                <FormattedMessage id={loginTextId} />
            </Button>
        );
    } else if (pending) {
        const classes = classNames(styles.primary, styles['action-button'], styles.pending);
        button = (
            <Button className={classes} disabled locale={locale} primary style={brandedButtonStyle}>
                <FormattedMessage id="bidding_console.console.button.pending" />
            </Button>
        );
    } else if (declined) {
        const classes = classNames(styles.tertiary, styles['action-button'], styles.submitted);
        const buttonText = <FormattedMessage id="bidding_console.console.button.declined" />;
        button = noLinks ? (
            <Button className={classes} disabled locale={locale} primary red>
                {buttonText}
            </Button>
        ) : (
            <Link className={styles['action-button']} locale={locale} to={getDeskSupportLink()}>
                {buttonText}
            </Link>
        );
    } else if (suspended || !accountInGoodStanding) {
        const classes = classNames(styles.tertiary, styles['action-button'], styles.submitted);
        const buttonText = <FormattedMessage id="bidding_console.console.button.suspended" />;
        button = noLinks ? (
            <Button className={classes} disabled locale={locale} primary red>
                {buttonText}
            </Button>
        ) : (
            <Link className={styles['action-button']} locale={locale} to={getDeskSupportLink()}>
                {buttonText}
            </Link>
        );
    } else if (limited || approved) {
        if (closed) {
            const classes = classNames(styles.tertiary, styles['action-button'], styles.submitted, {
                [styles.inactive]: !activeBidder,
                [styles.winning]: won,
            });
            button = (
                <Button className={classes} disabled locale={locale} primary red>
                    <FormattedMessage id="bidding_console.console.button.closed" />
                </Button>
            );
        } else if (myBid) {
            const classes = classNames(styles['action-button'], styles.winning);
            button = (
                <Button className={classes} locale={locale} primary>
                    <FormattedMessage id="bidding_console.console.button.winning" />
                </Button>
            );
        } else {
            const classes = classNames(styles.tertiary, styles['action-button'], {
                [styles.submitted]: submitted,
            });

            const hoverClasses = classNames(styles['hover-wrapper'], {
                [styles.countdown]: showCountDown && !submitted,
            });

            button = (
                <HoverIntent
                    onMouseOut={handleMouseOut(ready, wouldExceedBidLimit)}
                    onMouseOver={handleMouseOver(ready, wouldExceedBidLimit)}
                >
                    <div className={hoverClasses}>
                        {Boolean(toggleBiddingLocked) && (
                            <div className={styles.switchWrapper}>
                                <Switch
                                    activeBoxShadow={sliderActiveBoxShadow}
                                    boxShadow={sliderBoxShadow}
                                    checked={!biddingLocked}
                                    checkedIcon={uncheckedIcon}
                                    className={styles.switch}
                                    handleDiameter={handleDiameter}
                                    height={sliderHeight}
                                    id="material-switch"
                                    offColor={variables.grey100}
                                    onChange={toggleBiddingLocked}
                                    onColor={variables.teal}
                                    onHandleColor={variables.grey400}
                                    uncheckedIcon={checkedIcon}
                                    width={sliderWidth}
                                />
                            </div>
                        )}
                        <Button
                            className={classes}
                            disabled={submitted || !ready || wouldExceedBidLimit || biddingLocked || auctionPaused}
                            locale={locale}
                            onClick={placeBid}
                            primary
                            red
                        >
                            {submitted ? (
                                <FormattedMessage id="bidding_console.console.button.sent" />
                            ) : bidderOutbid ? (
                                outbidMessage
                            ) : (
                                bidMessage
                            )}
                        </Button>
                    </div>
                </HoverIntent>
            );
        }
    } else {
        const classes = classNames(styles.primary, styles['action-button']);
        button = (
            <ShouldLogInContainer
                modalContext={modalContext}
                requireCompleteProfile
                requireLogin
                runFunction={showRegisterForAuctionModal}
                runFunctionArgument={{ catalogId, fromBiddingConsole: true, itemId }}
            >
                <Button className={classes} locale={locale} primary style={brandedButtonStyle}>
                    <FormattedMessage id="bidding_console.console.button.register_long" />
                </Button>
            </ShouldLogInContainer>
        );
    }

    return button;
};

export default React.memo<Props>(BiddingConsoleButton, isEqual);
